import { t } from '../strings';

/**
 * The five Zoho Books rows Settings › Integrations › Zoho Books holds (piece 15;
 * decision 0922). 🔨 The client secret and the refresh token are sealed in the
 * KeyProvider envelope the way the Vision key is. The secret is read back as
 * held with its last characters; the refresh token is written by Connect with
 * Zoho alone and read back as held with no tail (decision 1465).
 */
export const ZOHO_BOOKS_CLIENT_ID_KEY = 'zohoBooks.clientId';
export const ZOHO_BOOKS_CLIENT_SECRET_KEY = 'zohoBooks.clientSecret';
export const ZOHO_BOOKS_REFRESH_TOKEN_KEY = 'zohoBooks.refreshToken';
export const ZOHO_BOOKS_ORGANIZATION_ID_KEY = 'zohoBooks.organizationId';
export const ZOHO_BOOKS_DATA_CENTRE_KEY = 'zohoBooks.dataCentre';

/** Every row the adapter needs, in the order the screen draws them. */
export const ZOHO_BOOKS_SETTING_KEYS: readonly string[] = [
  ZOHO_BOOKS_CLIENT_ID_KEY,
  ZOHO_BOOKS_CLIENT_SECRET_KEY,
  ZOHO_BOOKS_REFRESH_TOKEN_KEY,
  ZOHO_BOOKS_ORGANIZATION_ID_KEY,
  ZOHO_BOOKS_DATA_CENTRE_KEY,
];

/** The two sealed ones; they join the Settings sealed list. */
export const ZOHO_BOOKS_SEALED_KEYS: readonly string[] = [ZOHO_BOOKS_CLIENT_SECRET_KEY, ZOHO_BOOKS_REFRESH_TOKEN_KEY];

/**
 * The data centres Zoho Books runs in, each by the domain suffix its hosts
 * carry (the Multi-DC section of the v3 documentation, not opened here).
 */
export const ZOHO_BOOKS_DATA_CENTRES = ['com', 'eu', 'in', 'com.au', 'jp', 'ca', 'sa'] as const;
export type ZohoBooksDataCentre = (typeof ZOHO_BOOKS_DATA_CENTRES)[number];

export function isZohoBooksDataCentre(value: unknown): value is ZohoBooksDataCentre {
  return (ZOHO_BOOKS_DATA_CENTRES as readonly unknown[]).includes(value);
}

/**
 * The three hosts a data centre answers on: the sign-in host that exchanges a
 * refresh token, the API's own base, and the developer console a client is
 * registered in (T731; Zoho's manual names `https://api-console.zoho.com/` for
 * zoho.com, and the others follow the sign-in host's domain). ⚠️ Canada signs
 * in at `accounts.zohocloud.ca` as far as could be read here; piece 31's Open
 * issues carries the question until the first real pull answers it.
 */
export function zohoBooksHosts(dataCentre: ZohoBooksDataCentre): { accounts: string; api: string; console: string } {
  if (dataCentre === 'ca') return { accounts: 'https://accounts.zohocloud.ca', api: 'https://www.zohoapis.ca/books/v3', console: 'https://api-console.zohocloud.ca' };
  return { accounts: `https://accounts.zoho.${dataCentre}`, api: `https://www.zohoapis.${dataCentre}/books/v3`, console: `https://api-console.zoho.${dataCentre}` };
}

/**
 * 🔒 The Zoho Books web application's address for one organization (decision
 * 1971): the books host of the data centre, then `/app/` and the organization
 * ID. Canada follows the sign-in host's `zohocloud.ca`, with the same caveat.
 */
export function zohoBooksAppAddress(dataCentre: ZohoBooksDataCentre, organizationId: string): string {
  const host = dataCentre === 'ca' ? 'https://books.zohocloud.ca' : `https://books.zoho.${dataCentre}`;
  return `${host}/app/${encodeURIComponent(organizationId)}`;
}

/**
 * 🔨 The record pages whose address inside the books application is known
 * (decision 1971). An account, a tax and a manual journal are absent, so they
 * draw no link rather than a guessed one.
 */
const ZOHO_BOOKS_RECORD_PATH: Readonly<Partial<Record<string, string>>> = {
  Contact: 'contacts',
  Item: 'inventory/items',
  // 🔒 Decision 3340: the three kinds block 4 reads in the order of a sale, at the books application's own pages for them.
  Estimate: 'estimates',
  SalesOrder: 'salesorders',
  Invoice: 'invoices',
  CreditNote: 'creditnotes',
  CustomerPayment: 'paymentsreceived',
  PurchaseOrder: 'purchaseorders',
  Bill: 'bills',
  VendorCredit: 'vendorcredits',
  VendorPayment: 'paymentsmade',
  Expense: 'expenses',
};

/** 🔒 A file Zoho Books keeps on a record and offers no read of (decision 4805): its name, and the record's page in Zoho Books, or null where no address is held. */
export interface LeftInZohoFile {
  name: string;
  url: string | null;
}

/** One source record's page in Zoho Books, or null where no address is held or its kind's page is not known. */
export function zohoBooksRecordUrl(appAddress: string | null | undefined, sourceKind: string, sourceId: string): string | null {
  const path = ZOHO_BOOKS_RECORD_PATH[sourceKind];
  if (!appAddress || !path || sourceId.trim() === '') return null;
  return `${appAddress}#/${path}/${encodeURIComponent(sourceId)}`;
}

/** What a data centre reads as beside its picker. */
export const ZOHO_BOOKS_DATA_CENTRE_LABELS: Readonly<Record<ZohoBooksDataCentre, string>> = {
  com: t('zohoBooks.dc.com'),
  eu: t('zohoBooks.dc.eu'),
  in: t('zohoBooks.dc.in'),
  'com.au': t('zohoBooks.dc.comAu'),
  jp: t('zohoBooks.dc.jp'),
  ca: t('zohoBooks.dc.ca'),
  sa: t('zohoBooks.dc.sa'),
};
